/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Children, type ReactNode } from 'react';

import { cssMap as unboundCssMap } from '@compiled/react';

import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	list: {
		display: 'flex',
		isolation: 'isolate',
		marginBlockEnd: token('space.0'),
		marginBlockStart: token('space.0'),
		marginInlineStart: token('space.0'),
		paddingBlockEnd: token('space.0'),
		paddingBlockStart: token('space.0'),
		paddingInlineEnd: token('space.0'),
		paddingInlineStart: token('space.0'),
	},
	item: {
		marginBlockEnd: token('space.0'),
		marginBlockStart: token('space.0'),
		marginInlineStart: token('space.0'),
	},
});

// Icon rotations for the shuffled/notebook look — keyed by index.
// Matches the ICON_ROTATIONS map used in the Rovo chat button (SourceButtonIcons):
// index 1 → -6deg, index 3 → 8deg, all others → no rotation.
const ICON_ROTATIONS: Record<number, number> = {
	1: -6,
	3: 8,
};

const unboundStyles = unboundCssMap({
	list: {
		listStyleType: 'none',
		marginInlineEnd: '1px',
	},
	listEditorOverrides: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		paddingLeft: '0px !important',
	},
	item: {
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.small'),
		boxShadow: `0 0 0 1px ${token('color.border')}`,
		display: 'inline-flex',
		marginInlineEnd: '-1px',
	},
});

const DEFAULT_MAX_STACK_SIZE = 3;

export function IconStack({
	children,
	maxStackSize,
	testId,
	addEditorStyleOverrides,
	shuffled = false,
}: {
	children: ReactNode;
	maxStackSize?: number;
	testId?: string;
	addEditorStyleOverrides?: boolean;
	/** When true, applies the notebook-style shuffled look: ascending zIndex and per-icon rotations. */
	shuffled?: boolean;
}): JSX.Element {
	maxStackSize = maxStackSize ?? DEFAULT_MAX_STACK_SIZE;

	const childCount = Children.count(children);

	return (
		<ul
			aria-hidden
			css={[
				styles.list,
				unboundStyles.list,
				addEditorStyleOverrides && unboundStyles.listEditorOverrides,
			]}
			data-testid={testId}
		>
			{Children.map(
				children,
				(child, index) =>
					child && (
						<li
							css={[styles.item, unboundStyles.item]}
							style={{
								zIndex: shuffled ? index + 1 : maxStackSize - index,
								// The last icon should not pull inward, otherwise its 1px
								// box-shadow border gets clipped by the container/button edge.
								marginInlineEnd: index === childCount - 1 ? 0 : undefined,
								transform:
									shuffled && ICON_ROTATIONS[index] !== undefined
										? `rotate(${ICON_ROTATIONS[index]}deg)`
										: undefined,
							}}
						>
							{child}
						</li>
					),
			)}
		</ul>
	);
}
